import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, PartyPopper, Trash2, Sprout, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/store/AppContext";
import { Card, Overline, Button } from "@/components/ui-kit";
import { isThisWeek } from "@/lib/date";

const FridayReset: React.FC = () => {
  const { data, clearCompletedTasks, setWeeklyOrientations, markReset } = useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [orientations, setOrientations] = useState<string[]>(() =>
    [0, 1, 2].map((i) => data.weekly.items[i]?.text ?? "")
  );

  const setOrientationAt = (i: number, value: string) =>
    setOrientations((prev) => {
      const next = [...prev];
      next[i] = value;
      return next;
    });

  const doneTasks = useMemo(
    () => data.tasks.filter((t) => t.done && (!t.completedAt || isThisWeek(t.completedAt))),
    [data.tasks]
  );
  const doneBlocks = useMemo(
    () => data.workBlocks.filter((b) => b.done),
    [data.workBlocks]
  );
  const allDoneTasks = data.tasks.filter((t) => t.done);

  const finish = () => {
    setWeeklyOrientations(orientations);
    markReset();
    navigate("/today", { replace: true });
    toast.success("Belle semaine. Tes orientations sont posées.");
  };

  const steps = [
    // 0 — intro
    <div key="intro" className="pt-4">
      <div className="h-20 w-20 rounded-full bg-clay/15 flex items-center justify-center mb-8">
        <Sparkles strokeWidth={1.5} className="h-9 w-9 text-clay" />
      </div>
      <h1 className="font-heading text-4xl font-light tracking-tight text-forest-900 leading-tight mb-3">
        C'est l'heure du Reset.
      </h1>
      <p className="text-forest-500 leading-relaxed">
        Prends trois minutes pour clôturer ta semaine en douceur&nbsp;: célébrer, ranger, puis
        préparer la suite.
      </p>
    </div>,

    // 1 — celebrate
    <div key="celebrate">
      <Overline className="mb-4">Étape 1 · Célébrer</Overline>
      <h1 className="font-heading text-3xl font-light tracking-tight text-forest-900 leading-tight mb-6">
        Qu'as-tu accompli cette semaine&nbsp;?
      </h1>
      <Card className="p-6 mb-4 flex items-center gap-4">
        <PartyPopper strokeWidth={1.5} className="h-8 w-8 text-sage-dark shrink-0" />
        <div>
          <p className="font-heading text-2xl font-light text-forest-900">
            {doneTasks.length + doneBlocks.length}
          </p>
          <p className="text-sm text-forest-500">
            {doneTasks.length} tâche{doneTasks.length > 1 ? "s" : ""} · {doneBlocks.length} bloc
            {doneBlocks.length > 1 ? "s" : ""} de focus
          </p>
        </div>
      </Card>
      {doneTasks.length > 0 ? (
        <div className="space-y-2">
          {doneTasks.slice(0, 8).map((t) => (
            <div key={t.id} className="flex items-center gap-2.5 text-sm text-forest-700">
              <Check className="h-4 w-4 text-sage shrink-0" />
              <span className="line-through decoration-forest-500/40">{t.text}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-forest-500">
          Rien de coché cette semaine — et c'est très bien. Chaque semaine a son rythme.
        </p>
      )}
    </div>,

    // 2 — clean
    <div key="clean">
      <Overline className="mb-4">Étape 2 · Ranger</Overline>
      <h1 className="font-heading text-3xl font-light tracking-tight text-forest-900 leading-tight mb-6">
        On range les tâches terminées&nbsp;?
      </h1>
      {allDoneTasks.length > 0 ? (
        <>
          <Card className="p-5 mb-5">
            <div className="space-y-2">
              {allDoneTasks.map((t) => (
                <div key={t.id} className="flex items-center gap-2.5 text-sm text-forest-500">
                  <Check className="h-4 w-4 text-sage shrink-0" />
                  <span className="line-through">{t.text}</span>
                </div>
              ))}
            </div>
          </Card>
          <Button
            full
            variant="secondary"
            data-testid="clear-completed"
            onClick={() => {
              clearCompletedTasks();
              toast.success("Espace nettoyé.");
            }}
          >
            <Trash2 className="h-5 w-5" /> Archiver les {allDoneTasks.length} tâches terminées
          </Button>
        </>
      ) : (
        <Card className="p-8 text-center">
          <p className="text-forest-500">Ton espace est déjà net. Rien à ranger.</p>
        </Card>
      )}
    </div>,

    // 3 — plan
    <div key="plan">
      <Overline className="mb-4">Étape 3 · Orienter</Overline>
      <h1 className="font-heading text-3xl font-light tracking-tight text-forest-900 leading-tight mb-3">
        Tes 3 orientations pour la semaine prochaine&nbsp;?
      </h1>
      <p className="text-forest-500 leading-relaxed mb-6">
        Trois caps, en douceur. Ils t'accompagneront chaque jour, en haut de l'écran Aujourd'hui.
      </p>
      <div className="bg-white rounded-3xl border border-forest-900/5 shadow-[0_8px_32px_rgba(33,38,33,0.04)] px-6 py-2">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={`flex items-center gap-4 py-4 ${i < 2 ? "border-b border-forest-900/5" : ""}`}
          >
            <Sprout strokeWidth={1.5} className="h-5 w-5 text-sage shrink-0" />
            <input
              value={orientations[i]}
              onChange={(e) => setOrientationAt(i, e.target.value)}
              data-testid={`orientation-input-${i}`}
              placeholder={`Orientation ${i + 1}…`}
              className="w-full bg-transparent border-0 p-0 focus:outline-none text-forest-800 text-[15px] leading-relaxed placeholder:text-forest-500/40"
            />
          </div>
        ))}
      </div>
    </div>,
  ];

  const isLast = step === steps.length - 1;

  return (
    <div>
      <div className="flex items-center gap-2 mb-10">
        {steps.map((s, i) => (
          <span
            key={s.key ?? i}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i === step ? "w-8 bg-clay" : i < step ? "w-4 bg-clay/40" : "w-4 bg-forest-900/10"
            }`}
          />
        ))}
      </div>

      <div className="min-h-[46vh]">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {steps[step]}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex items-center gap-3 mt-10">
        {step > 0 && (
          <button
            onClick={() => setStep((s) => s - 1)}
            data-testid="reset-back"
            aria-label="Précédent"
            className="h-14 w-14 rounded-full bg-white border border-forest-900/10 flex items-center justify-center text-forest-700 shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        {!isLast ? (
          <Button full onClick={() => setStep((s) => s + 1)} data-testid="reset-next">
            Continuer
            <ArrowRight className="h-5 w-5" />
          </Button>
        ) : (
          <Button full onClick={finish} data-testid="reset-finish">
            Clôturer ma semaine
            <Check className="h-5 w-5" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default FridayReset;
